/**
 * MONAI Mega Menu - JavaScript
 * Click-only navigation (no hover)
 * Mobile-responsive with hamburger toggle
 * 
 * Compatible with Impulse Theme - prevents conflicts with theme.js event handlers
 */

(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.monaiMegaMenuInitialized) {
    console.log('MONAI Menu: Already initialized, skipping');
    return;
  }
  window.monaiMegaMenuInitialized = true;

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaMenu);
  } else {
    // DOM is already ready
    initMegaMenu();
  }

  function initMegaMenu() {
    const menu = document.getElementById('monai-mega-menu');
    const mobileToggle = document.querySelector('.monai-mobile-toggle');
    
    if (!menu) {
      console.warn('MONAI Menu: #monai-mega-menu not found');
      return;
    }

    console.log('MONAI Menu: Initialized');

    // Get all mega menu toggle buttons
    const megaMenuToggles = menu.querySelectorAll('.mega-menu-toggle');

    // ========================================
    // Helper function: Check if mobile view
    // ========================================
    function isMobile() {
      return window.innerWidth <= 991;
    }

    // ========================================
    // Helper function: Close all mega menus
    // ========================================
    function closeAllMegaMenus(except) {
      const openMegaMenus = menu.querySelectorAll('.nav-item.has-mega-menu.show');
      openMegaMenus.forEach(function(item) {
        if (item !== except) {
          item.classList.remove('show');
          const megaMenuPanel = item.querySelector('.mega-menu-panel');
          if (megaMenuPanel) {
            megaMenuPanel.classList.remove('show');
          }
          const toggle = item.querySelector('.mega-menu-toggle');
          if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
          }
        }
      });
    }

    // ========================================
    // UNIFIED: Click handler for mega menu toggles
    // Uses capturing phase and stopImmediatePropagation to prevent
    // Impulse theme.js handlers from interfering
    // ========================================
    megaMenuToggles.forEach(function(toggle) {
      // Use capture phase to handle event before it bubbles
      toggle.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent other click handlers on same element

        const navItem = this.closest('.nav-item.has-mega-menu');
        const megaMenuPanel = navItem.querySelector('.mega-menu-panel');

        if (isMobile()) {
          // ========================================
          // MOBILE: Toggle mobile-expanded class
          // ========================================
          const isExpanded = navItem.classList.contains('mobile-expanded');

          // Close siblings
          const siblings = navItem.parentElement.querySelectorAll('.nav-item.has-mega-menu.mobile-expanded');
          siblings.forEach(function(sibling) {
            if (sibling !== navItem) {
              sibling.classList.remove('mobile-expanded');
              const siblingToggle = sibling.querySelector('.mega-menu-toggle');
              if (siblingToggle) {
                siblingToggle.setAttribute('aria-expanded', 'false');
              }
            }
          });

          // Toggle current
          if (isExpanded) {
            navItem.classList.remove('mobile-expanded');
            this.setAttribute('aria-expanded', 'false');
            console.log('MONAI Menu: Closed mobile menu');
          } else {
            navItem.classList.add('mobile-expanded');
            this.setAttribute('aria-expanded', 'true');
            console.log('MONAI Menu: Opened mobile menu');
          }
        } else {
          // ========================================
          // DESKTOP: Toggle show class
          // ========================================
          const isOpen = navItem.classList.contains('show');

          // Close all other open menus first
          closeAllMegaMenus(navItem);

          // Toggle this mega menu
          if (!isOpen) {
            navItem.classList.add('show');
            megaMenuPanel.classList.add('show');
            this.setAttribute('aria-expanded', 'true');
            console.log('MONAI Menu: Opened mega menu');
          } else {
            navItem.classList.remove('show');
            megaMenuPanel.classList.remove('show');
            this.setAttribute('aria-expanded', 'false');
            console.log('MONAI Menu: Closed mega menu');
          }
        }
      }, true); // true = use capture phase
    });

    // ========================================
    // Prevent clicks inside mega menu panels from closing menu
    // This stops event bubbling to document handlers
    // ========================================
    menu.querySelectorAll('.mega-menu-panel').forEach(function(panel) {
      panel.addEventListener('click', function(e) {
        // Only stop propagation, don't prevent default (allow links to work)
        e.stopPropagation();
      }, true);
    });

    // ========================================
    // Close mega menus when clicking outside
    // Use setTimeout to allow the toggle click to complete first
    // ========================================
    document.addEventListener('click', function(e) {
      // Skip if click originated from inside our menu system
      if (menu.contains(e.target) || mobileToggle?.contains(e.target)) {
        return;
      }
      
      // Skip if Impulse theme has a drawer open (don't interfere)
      if (document.documentElement.classList.contains('js-drawer-open')) {
        return;
      }

      // Use setTimeout to ensure this runs after any toggle handlers
      setTimeout(function() {
        closeAllMegaMenus();
        // Also close mobile expanded items
        const mobileExpanded = menu.querySelectorAll('.nav-item.has-mega-menu.mobile-expanded');
        mobileExpanded.forEach(function(item) {
          item.classList.remove('mobile-expanded');
          const toggle = item.querySelector('.mega-menu-toggle');
          if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
          }
        });
      }, 0);
    });

    // ========================================
    // Close mega menus on Escape key
    // ========================================
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        closeAllMegaMenus();
        // Close mobile expanded items
        const mobileExpanded = menu.querySelectorAll('.nav-item.has-mega-menu.mobile-expanded');
        mobileExpanded.forEach(function(item) {
          item.classList.remove('mobile-expanded');
          const toggle = item.querySelector('.mega-menu-toggle');
          if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
          }
        });
        // Also close mobile menu
        if (menu.classList.contains('mobile-open')) {
          menu.classList.remove('mobile-open');
          if (mobileToggle) {
            mobileToggle.classList.remove('active');
            mobileToggle.setAttribute('aria-expanded', 'false');
          }
          document.body.classList.remove('menu-open');
        }
      }
    });

    // ========================================
    // MOBILE: Hamburger toggle
    // ========================================
    if (mobileToggle) {
      mobileToggle.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const isOpen = menu.classList.contains('mobile-open');

        if (isOpen) {
          menu.classList.remove('mobile-open');
          this.classList.remove('active');
          this.setAttribute('aria-expanded', 'false');
          document.body.classList.remove('menu-open');
        } else {
          menu.classList.add('mobile-open');
          this.classList.add('active');
          this.setAttribute('aria-expanded', 'true');
          document.body.classList.add('menu-open');
        }
      }, true); // Use capture phase
    }

    // ========================================
    // Handle resize: Reset states on breakpoint change
    // ========================================
    let resizeTimer;
    let lastWidth = window.innerWidth;
    
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        const currentWidth = window.innerWidth;
        const crossedBreakpoint = (lastWidth <= 991 && currentWidth > 991) || (lastWidth > 991 && currentWidth <= 991);
        
        if (crossedBreakpoint) {
          // Reset all states when crossing the mobile/desktop breakpoint
          closeAllMegaMenus();
          
          // Close mobile menu when switching to desktop
          menu.classList.remove('mobile-open');
          if (mobileToggle) {
            mobileToggle.classList.remove('active');
            mobileToggle.setAttribute('aria-expanded', 'false');
          }
          document.body.classList.remove('menu-open');
          
          // Remove mobile-expanded classes
          const expanded = menu.querySelectorAll('.mobile-expanded');
          expanded.forEach(function(item) {
            item.classList.remove('mobile-expanded');
            const toggle = item.querySelector('.mega-menu-toggle');
            if (toggle) {
              toggle.setAttribute('aria-expanded', 'false');
            }
          });
        }
        
        lastWidth = currentWidth;
      }, 150);
    });

    // ========================================
    // Accessibility: Focus management
    // ========================================
    // Trap focus inside open mega menu for keyboard users
    menu.addEventListener('keydown', function(e) {
      if (e.key === 'Tab') {
        const openMegaMenu = menu.querySelector('.nav-item.has-mega-menu.show .mega-menu-panel');
        if (openMegaMenu) {
          const focusableElements = openMegaMenu.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          
          if (focusableElements.length > 0) {
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey && document.activeElement === firstElement) {
              // Shift+Tab from first element -> close mega menu
              e.preventDefault();
              closeAllMegaMenus();
              const toggle = menu.querySelector('.nav-item.has-mega-menu .mega-menu-toggle[aria-expanded="true"]');
              if (toggle) {
                toggle.focus();
              }
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              // Tab from last element -> move to next nav item
              e.preventDefault();
              closeAllMegaMenus();
              const currentNavItem = lastElement.closest('.nav-item.has-mega-menu');
              const nextNavItem = currentNavItem.nextElementSibling;
              if (nextNavItem) {
                const nextToggle = nextNavItem.querySelector('.nav-link, .mega-menu-toggle');
                if (nextToggle) {
                  nextToggle.focus();
                }
              }
            }
          }
        }
      }
    });

  } // End initMegaMenu

})();
